import { BaseMeiucaService } from './MeiucaService'
import { env, apiEndpoints } from '@/lib/env'

// ---------------------------------------------------------------------------
// Types — mirror the `meiuca_app/v1/me/venue-memberships` DTOs
// ---------------------------------------------------------------------------

/** How the venue collects. `manual` = no static Pix key on the arena side —
 *  the app renders "pague na arena" (product decision, epic #66). */
export type VenueMembershipPaymentType = 'pixOnline' | 'manual'

export interface VenueMembershipPixInfo {
  payload: string
  encodedImage: string
  expirationDate?: string | null
  invoiceUrl?: string | null
}

/** An open monthly charge (cycle), already carrying the Pix when available. */
export interface VenueMembershipOutstandingPaymentItem {
  id: string
  cycleYear: number
  cycleMonth: number
  amountCents: number
  dueAt?: string | null
  paymentType: VenueMembershipPaymentType
  pix?: VenueMembershipPixInfo | null
}

export interface VenueMembershipItem {
  id: string
  venueId: string
  venueName: string
  /** 1 = domingo … 7 = sábado (same scale as `DayOfWeek` in web-foundation). */
  weekday: number
  totalAmountCents: number
  status: string
  startDate: string
  /** Set once the membership ends; in the past = "Finalizado". */
  endDate?: string | null
  dueAt?: string | null
  paymentType: VenueMembershipPaymentType
  outstandingPayments: VenueMembershipOutstandingPaymentItem[]
}

/** Payer-facing status of a cycle: `pending` covers open + processing. */
export type VenueMembershipCycleStatus = 'paid' | 'pending' | 'failed' | 'canceled'

/** One monthly charge (cycle) of the membership — the "Mensalidades" history. */
export interface VenueMembershipCycleItem {
  id: string
  cycleYear: number
  cycleMonth: number
  startDate: string
  endDate?: string | null
  totalAmountCents: number
  status: VenueMembershipCycleStatus
  dueAt?: string | null
  paidAt?: string | null
}

export type VenueMembershipListScope = 'active' | 'ended'

class VenueMembershipService extends BaseMeiucaService {
  constructor() {
    super({
      baseURL: env.API_URL,
      refreshEndpoint: apiEndpoints.auth.refresh,
      cookiePrefix: '',
      debug: env.IS_DEVELOPMENT,
      onAuthenticationError: () => {
        if (typeof window !== 'undefined') {
          window.location.replace('/')
        }
      }
    })
  }

  /**
   * Memberships of the account across venues, open charges embedded.
   * `active` (default) = current + upcoming; `ended` = history.
   */
  async listMemberships(status: VenueMembershipListScope = 'active'): Promise<VenueMembershipItem[]> {
    const response = await this.get<{ data: VenueMembershipItem[] }>(
      `/me/venue-memberships?status=${status}`
    )
    return response.data.data
  }

  /** Light endpoint polled by the payment screen until it empties out. */
  async listOutstandingPayments(membershipId: string): Promise<VenueMembershipOutstandingPaymentItem[]> {
    const response = await this.get<{ data: VenueMembershipOutstandingPaymentItem[] }>(
      `/me/venue-memberships/${encodeURIComponent(membershipId)}/outstanding-payments`
    )
    return response.data.data
  }

  /** Every cycle of the membership, newest first (flat — one cycle per month). */
  async listCycles(membershipId: string): Promise<VenueMembershipCycleItem[]> {
    const response = await this.get<{ data: VenueMembershipCycleItem[] }>(
      `/me/venue-memberships/${encodeURIComponent(membershipId)}/cycles`
    )
    return response.data.data
  }
}

export const venueMembershipService = new VenueMembershipService()
